import React, {useCallback, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_RESEND_VIDEO_BUFFER_DURATION,
  GIFT_SHOP_RESEND_VIDEO_MAX_DURATION,
  GIFT_SHOP_RESEND_VIDEO_MAX_RESOLUTION,
  GIFT_SHOP_RESEND_VIDEO_MAX_SIZE,
  GIFT_SHOP_UPLOAD_VIDEO_MIME_EXTENSION_MAP,
  GIFT_SHOP_UPLOAD_VIDEO_TYPE,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {GiftshopTemplateUploadResendModal} from 'pages'
import {
  GiftshopResendDataType,
  GiftshopResendModalState,
  GiftshopUploadContentData,
  VideoMetadata,
  WindowModeType,
} from 'types'
import {
  clearUserCache,
  convertFileSizeFromByte,
  getBorder,
  getContentURL,
  getUniqueId,
  showSnackbar,
  useDebounce,
  useDidMount,
  useDidUpdate,
  useGiftShopResendVideoHandler,
  useHistory,
} from 'utils'
import {useWindowMode} from 'windows'
import {Button, ButtonOutline, InputPicker, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopProfileResendVideoProps} from './GiftShopProfileResendVideoProps'
import GiftShopProfileResendVideoItemHiRes from './GiftShopProfileResendVideoItemHiRes'
import GiftShopProfileResendVideoItemStandard from './GiftShopProfileResendVideoItemStandard'

interface StyledFooterContainerProps {
  mode: WindowModeType
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
  scrollbar-width: auto;
  scrollbar-color: ${({theme}) => `${theme.primary_5} ${theme.white_3}`};
  ::-webkit-scrollbar {
    width: ${convertUnit(25)};
    background-color: ${({theme}) => theme.white_3};
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({theme}) => theme.primary_5};
    background-clip: content-box;
    border: ${convertUnit(8)} solid ${({theme}) => theme.white_1};
    border-radius: ${convertUnit(16)};
  }
`

const StyledHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: ${convertUnit(12)} 0;
  margin-bottom: ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin-bottom: ${convertUnit(1)};
  }
`

const StyledHeaderItem = styled(Paragraph)`
  width: 50%;
  text-align: center;
`

const StyledContentsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  padding: 0 ${convertUnit(20)};
  margin-bottom: ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    gap: ${convertUnit(4)};
    padding: 0;
    margin-bottom: ${convertUnit(4)};
  }
`

const StyledFooterContainer = styled.div<StyledFooterContainerProps>`
  ${({theme, mode}) => ({
    borderTop: getBorder(1, 'solid', theme.gray_1),
    padding: `${convertUnit(15)} ${convertUnit(mode === 'mobile' ? 25 : 50)}`,
    backgroundColor: theme.white_1,
  })}
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  width: 100%;
  align-items: center;
  gap: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  flex: 1;
  width: 100%;
`

const StyledAddVideoButton = styled(ButtonOutline)`
  flex: 1;
  width: 100%;
`

const StyledTryAgain = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  margin-top: ${convertUnit(20)};
`

export default function GiftShopProfileResendVideo({
  data,
  source,
}: GiftShopProfileResendVideoProps) {
  const {translate} = useTranslation()
  const {
    video_url,
    title,
    original_at,
    size,
    duration,
    creator_name,
    type,
  } = data
  const videoExistingRef = useRef<HTMLVideoElement>(null)
  const videoHiResRef = useRef<HTMLVideoElement>(null)
  const [resendFile, setResendFile] = useState<File>()
  const [resendData, setResendData] = useState<GiftshopResendDataType>()

  const mode = useWindowMode()
  const resendRef = useRef<HTMLInputElement>(null)
  const resendModalState = useState<GiftshopResendModalState>('none')
  const setResendModal = resendModalState[1]
  const history = useHistory()
  const [success, setSuccess] = useState(0)
  const debounce = useDebounce()
  const {uploadResend} = useGiftShopResendVideoHandler()

  const handleResend = useCallback(async () => {
    setResendModal('loading')
    const isSuccess =
      resendData && (await uploadResend(resendData.image, resendData.id))
    if (isSuccess) {
      setSuccess((prev) => prev + 1)
    } else {
      setResendModal('failed')
    }
  }, [resendData, setResendModal, uploadResend])

  const handleReadFile = useCallback(
    async (file: File) => {
      if (!GIFT_SHOP_UPLOAD_VIDEO_TYPE.includes(file.type)) {
        showSnackbar(translate('giftShop:videoUploadErrorInvalidFormat'))
        return
      }

      if (file.size > GIFT_SHOP_RESEND_VIDEO_MAX_SIZE) {
        showSnackbar(
          translate('giftShop:videoUploadErrorMaxSize', {
            maxSize: convertFileSizeFromByte(
              GIFT_SHOP_RESEND_VIDEO_MAX_SIZE,
              'MB',
            ),
          }),
        )
        return
      }

      setResendFile(file)
    },
    [translate],
  )

  const handleSetResendData = useCallback(
    (metadata: VideoMetadata, objectUrl: string) => {
      const maxDuration =
        GIFT_SHOP_RESEND_VIDEO_MAX_DURATION +
        GIFT_SHOP_RESEND_VIDEO_BUFFER_DURATION

      if (!resendFile) return

      if (metadata.duration > maxDuration) {
        setResendData(undefined)
        setResendFile(undefined)
        showSnackbar(
          translate('giftShop:videoUploadErrorMaxDuration', {
            duration: Math.floor(maxDuration),
          }),
        )
        return
      }

      if (
        metadata.width > GIFT_SHOP_RESEND_VIDEO_MAX_RESOLUTION ||
        metadata.height > GIFT_SHOP_RESEND_VIDEO_MAX_RESOLUTION
      ) {
        setResendData(undefined)
        setResendFile(undefined)
        showSnackbar(
          translate('giftShop:videoUploadErrorMaxResolution', {
            maxRes: GIFT_SHOP_RESEND_VIDEO_MAX_RESOLUTION,
          }),
        )
        return
      }

      if (resendFile.name.toLowerCase() !== title.toLowerCase()) {
        showSnackbar(translate('giftShop:resendContentDifferentFile'))
      }

      const image: GiftshopUploadContentData = {
        id: getUniqueId(),
        file: resendFile,
        src: objectUrl,
        size: resendFile.size,
        duration: Math.floor(metadata.duration),
        width: metadata.width,
        height: metadata.height,
        extension:
          GIFT_SHOP_UPLOAD_VIDEO_MIME_EXTENSION_MAP[resendFile.type] ?? 'mp4',
      }

      setResendData({
        id: data.content_id,
        image,
      })
    },
    [data.content_id, resendFile, title, translate],
  )

  const handleRenderHeader = useMemo(
    () => (
      <StyledHeaderContainer>
        <StyledHeaderItem
          fontSize={mode === 'website' ? 'm' : 's'}
          fontWeight="bold"
          color="primary_5">
          {translate('global:standard')}
        </StyledHeaderItem>
        <StyledHeaderItem
          fontSize={mode === 'website' ? 'm' : 's'}
          fontWeight="bold"
          color="primary_5">
          {translate('global:hires')}
        </StyledHeaderItem>
      </StyledHeaderContainer>
    ),
    [mode, translate],
  )

  const handleRenderExistingContent = useMemo(
    () =>
      typeof original_at === 'string' ? (
        <GiftShopProfileResendVideoItemStandard
          videoRef={videoExistingRef}
          date={original_at}
          duration={duration}
          size={size}
          src={getContentURL({url: video_url, type})}
          title={title}
          isNewContent={false}
        />
      ) : (
        <></>
      ),
    [duration, original_at, size, title, type, video_url],
  )

  const handleRenderNewContent = useMemo(
    () => (
      <GiftShopProfileResendVideoItemHiRes
        videoRef={videoHiResRef}
        resendData={resendData}
        resendFile={resendFile}
        onVideoLoaded={handleSetResendData}
      />
    ),
    [handleSetResendData, resendData, resendFile],
  )

  const handleRenderContent = useMemo(
    () => (
      <StyledContentsContainer>
        {handleRenderExistingContent}
        {handleRenderNewContent}
      </StyledContentsContainer>
    ),
    [handleRenderExistingContent, handleRenderNewContent],
  )

  const handleRenderButton = useMemo(
    () => (
      <StyledFooterContainer mode={mode}>
        <StyledAddVideoButton
          label={
            resendData?.image.src
              ? translate('giftShop:videoResendChangeButton')
              : translate('giftShop:videoResendAddButton')
          }
          borderColor="gray_5"
          color="gray_5"
          onClick={() => resendRef.current?.click()}
        />
        <StyledButton
          disabled={resendData === undefined}
          label={translate('global:upload')}
          backgroundColor="primary_5"
          onClick={handleResend}
        />
      </StyledFooterContainer>
    ),
    [handleResend, mode, resendData, translate],
  )

  const handleRenderTryAgain = useMemo(
    () => (
      <StyledTryAgain>
        <StyledButton
          label={translate('global:close')}
          color="primary_5"
          backgroundColor="white_2"
          onClick={() =>
            history.replace('giftshop_profile', {self: true}, creator_name)
          }
        />
        <StyledButton
          label={translate('global:tryAgain')}
          onClick={() => setResendModal('none')}
        />
      </StyledTryAgain>
    ),
    [creator_name, history, setResendModal, translate],
  )

  const handleRenderModalResendStatus = useMemo(
    () => (
      <GiftshopTemplateUploadResendModal
        resendModalState={resendModalState}
        bottomElement={handleRenderTryAgain}
        success={success}
        total={1}
      />
    ),
    [handleRenderTryAgain, resendModalState, success],
  )

  const handleRenderInputPicker = useMemo(
    () => (
      <InputPicker
        inputRef={resendRef}
        onLoadFiles={(files) => handleReadFile(files[0])}
        accept={GIFT_SHOP_UPLOAD_VIDEO_TYPE.join(',')}
      />
    ),
    [handleReadFile],
  )

  useDidUpdate(() => {
    if (success === 1) {
      setResendModal('success')
      clearUserCache('creation')
      debounce(() => {
        if (source === 'host') {
          history.replace('tree_host_profile', {})
        } else history.replace('giftshop_profile', {self: true}, creator_name)
      }, 500)
    }
  }, [success, history])

  useDidMount(() => {
    resendRef.current && resendRef.current.click()
  })

  return (
    <>
      {handleRenderModalResendStatus}
      {handleRenderInputPicker}
      <StyledContainer>
        {handleRenderHeader}
        {handleRenderContent}
      </StyledContainer>
      {handleRenderButton}
    </>
  )
}
